import {DateObject} from 'react-multi-date-picker'
import gregorian from 'react-date-object/calendars/gregorian'
import persian from 'react-date-object/calendars/persian'

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'

export const toPersianDigits = (value: string | number | null | undefined) =>
  value === null || value === undefined
    ? ''
    : String(value).replace(/\d/g, (digit) => PERSIAN_DIGITS[Number(digit)])

export const display = (value: unknown) =>
  value === null || value === undefined || value === '' ? '—' : String(value)

export const formatRial = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  const rounded = Math.floor(Number(value))
  const sign = rounded < 0 ? '−' : ''
  const grouped = String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, '٬')
  return `${sign}${toPersianDigits(grouped)}`
}

export const formatSignedRial = (value: number | null | undefined) =>
  value === null || value === undefined
    ? '—'
    : `${Number(value) > 0 ? '+' : ''}${formatRial(value)}`

const parseUtc = (value: string | number | Date | null | undefined) => {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return value
  if (typeof value === 'number') return new Date(value)
  // The API serialises DateTime.UtcNow without a zone suffix; treat naked values as UTC.
  const text = /[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`
  const parsed = new Date(text)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const formatDateTime = (
  value: string | number | Date | null | undefined,
  withTime = true
) => {
  const parsed = parseUtc(value)
  if (!parsed) return '—'
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? {hour: '2-digit', minute: '2-digit'} : {}),
  }).format(parsed)
}

export const formatRelative = (
  value: string | number | Date | null | undefined,
  now = Date.now()
) => {
  const parsed = parseUtc(value)
  if (!parsed) return '—'
  const seconds = Math.max(0, Math.round((now - parsed.getTime()) / 1000))
  if (seconds < 60) return `${toPersianDigits(seconds)} ثانیه پیش`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${toPersianDigits(minutes)} دقیقه پیش`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${toPersianDigits(hours)} ساعت پیش`
  return formatDateTime(parsed)
}

/** Current Persian fiscal year and quarter for the default filter. */
export const currentFiscalPeriod = (now: Date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US-u-ca-persian', {
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(now)
  const year = Number(parts.find((part) => part.type === 'year')?.value || 0)
  const month = Number(parts.find((part) => part.type === 'month')?.value || 1)
  return {year, quarter: Math.ceil(month / 3)}
}

export const QUARTER_LABELS: Record<number, string> = {
  1: 'بهار',
  2: 'تابستان',
  3: 'پاییز',
  4: 'زمستان',
}

/**
 * Values that come from the customer's own database (log rows) are wall-clock times of that
 * server, not UTC; they are shown exactly as stored, converted to the Persian calendar only.
 */
export const formatLocalDateTime = (value: string | null | undefined, withTime = true) => {
  if (!value) return '—'
  const naked = value.replace(/[zZ]$|[+-]\d{2}:\d{2}$/, '')
  const parsed = new Date(`${naked}Z`)
  if (Number.isNaN(parsed.getTime())) return value
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? {hour: '2-digit', minute: '2-digit'} : {}),
  }).format(parsed)
}

export const normalizeDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))

/** "۱۴۰۵/۰۶/۲۰" → "2026-09-11" (a naked date the API reads as a server-local day). */
export const persianToIsoDate = (value: string): string => {
  const normalized = normalizeDigits(value.trim()).replace(/-/g, '/')
  if (!/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(normalized)) {
    throw new Error('تاریخ را با قالب ۱۴۰۵/۰۶/۲۰ وارد کنید.')
  }
  const [year, month, day] = normalized.split('/').map(Number)
  const monthLengths = persian.getMonthLengths(persian.isLeap(year))
  if (month < 1 || month > 12 || day < 1 || day > monthLengths[month - 1]) {
    throw new Error('تاریخ شمسی معتبر نیست.')
  }
  const converted = new DateObject({calendar: persian, year, month, day}).convert(gregorian)
  return `${converted.year}-${String(converted.month.number).padStart(2, '0')}-${String(
    converted.day
  ).padStart(2, '0')}`
}

const FIRST_STRONG_ISOLATE = String.fromCharCode(0x2068)
const POP_DIRECTIONAL_ISOLATE = String.fromCharCode(0x2069)

/**
 * A Latin identifier inside a Persian sentence keeps its own order: «565621-R1» read as «R1-565621» and a number after a
 * «»-quoted name jumped places without it.
 */
export const isolate = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === ''
    ? ''
    : `${FIRST_STRONG_ISOLATE}${value}${POP_DIRECTIONAL_ISOLATE}`

/**
 * A sentence the server wrote, with each run of Latin letters, digits and signs that holds a letter, «-» or «/» isolated
 * the same way (an English error stays one readable run); plain numbers are left as they are.
 */
export const isolateLatinRuns = (text: string | null | undefined) =>
  (text || '').replace(/[A-Za-z0-9](?:[\x20-\x7E]*[A-Za-z0-9)\]])?/g, (run) =>
    /[A-Za-z]|[-/]/.test(run) ? isolate(run) : run
  )
